import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  IconButton,
  Box,
  Card,
  CardContent,
  Avatar,
  Tab,
  Tabs,
  Dialog,
  useTheme,
} from "@material-ui/core";

import {
  CloudUpload as CloudUploadIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  WhatsApp as WhatsAppIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  InsertEmoticon as InsertEmoticonIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import moment from "moment";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { head } from "lodash";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import planilhaExemplo from "../../assets/planilha.xlsx";
import ModernDateTimePicker from "../../components/ModernDateTimePicker";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(3),
  },
  wizardCard: {
    borderRadius: "20px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: theme.palette.type === 'light' ? "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)" : theme.palette.background.paper,
    overflow: "hidden",
    marginTop: theme.spacing(4),
  },

  header: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(4),
    color: "white",
    textAlign: "center",
  },
  stepper: {
    padding: theme.spacing(4, 2),
    background: "transparent",
  },
  stepContent: {
    padding: theme.spacing(4),
    minHeight: "400px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(3, 4),
    background: theme.palette.type === 'light' ? "#fdfdfd" : "rgba(255, 255, 255, 0.05)",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  dropzone: {
    border: "2px dashed #6517ab",
    borderRadius: "20px",
    padding: theme.spacing(6),
    textAlign: "center",
    backgroundColor: theme.palette.type === 'light' ? "#f4f0ff" : "rgba(101, 23, 171, 0.1)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    width: "100%",
    maxWidth: "500px",
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' ? "#ede7ff" : "rgba(101, 23, 171, 0.2)",
      borderColor: "#8e44ad",
      transform: "scale(1.02)",
    },
  },

  uploadIcon: {
    fontSize: "64px",
    color: "#6517ab",
    marginBottom: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
    },
  },
  btnPrimary: {
    borderRadius: "12px",
    padding: "12px 24px",
    textTransform: "none",
    fontWeight: "600",
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    color: "white",
    boxShadow: "0 4px 15px rgba(101, 23, 171, 0.3)",
    "&:hover": {
      background: "linear-gradient(90deg, #53118d 0%, #7d3c98 100%)",
    },
  },
  btnSecondary: {
    borderRadius: "12px",
    padding: "12px 24px",
    textTransform: "none",
    fontWeight: "600",
  },
  templateLink: {
    marginTop: theme.spacing(2),
    color: "#6517ab",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontWeight: "500",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  successIcon: {
    fontSize: "80px",
    color: "#2ecc71",
    marginBottom: theme.spacing(2),
  },
  warningContainer: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'light' ? "#fffced" : "#332b00",
    borderBottom: `1px solid ${theme.palette.type === 'light' ? "#ffeeba" : "#4d4100"}`,
    display: "flex",

    flexDirection: "column",
    gap: theme.spacing(2),
  },
  warningTitle: {
    color: theme.palette.type === 'light' ? "#856404" : "#ffc107",
    fontWeight: "bold",
    fontSize: "1.1rem",

    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  warningText: {
    color: theme.palette.type === 'light' ? "#856404" : "#e0a800",
    fontSize: "0.95rem",
    lineHeight: "1.6",
  },

  warningList: {
    margin: theme.spacing(1, 0),
    paddingLeft: theme.spacing(3),
    "& li": {
      color: "#856404",
      fontSize: "0.9rem",
      marginBottom: theme.spacing(0.5),
    },
  },
}));

const ListSchema = Yup.object().shape({
  name: Yup.string().required("O nome da lista é obrigatório"),
});

const CampaignSchema = Yup.object().shape({
  name: Yup.string().required("O nome da campanha é obrigatório"),
  message1: Yup.string().required("A mensagem é obrigatória"),
  whatsappId: Yup.string().required("Selecione uma conexão"),
  scheduledAt: Yup.string().required("O agendamento é obrigatório"),
});

const CampaignWizard = () => {
  const theme = useTheme();
  const classes = useStyles();

  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contactListId, setContactListId] = useState(null);
  const [contactListName, setContactListName] = useState("");
  const [file, setFile] = useState(null);
  const [whatsapps, setWhatsapps] = useState([]);
  const [campaignData, setCampaignData] = useState({
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    whatsappId: "",
    scheduledAt: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
    confirmation: false,
  });

  const [showWarning, setShowWarning] = useState(false);
  const [pendingListValues, setPendingListValues] = useState(null);

  const steps = ["Criar Lista", "Importar Contatos", "Configurar Envio"];

  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp", {
          params: { companyId: user.companyId },
        });
        setWhatsapps(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
  }, [user.companyId]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // DRAG & DROP
  const onDrop = (acceptedFiles) => {
    setFile(acceptedFiles[0]);
    toast.info(`Arquivo ${acceptedFiles[0].name} selecionado!`);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xlsx, .csv",
    multiple: false,
  });

  // STEP 1: CREATE LIST
  const handleCreateList = (values) => {
    setPendingListValues(values);
    setShowWarning(true);
  };

  const confirmCreateList = async () => {
    setShowWarning(false);
    setLoading(true);
    try {
      const { data } = await api.post("/contact-lists", {
        name: pendingListValues.name,
        companyId: user.companyId,
      });
      setContactListId(data.id);
      setContactListName(data.name);
      handleNext();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: UPLOAD CONTACTS
  const handleUploadContacts = async () => {
    if (!file) {
      toast.warning("Por favor, selecione um arquivo.");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/contact-lists/${contactListId}/upload`, formData);
      toast.success("Contatos importados com sucesso!");
      handleNext();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // STEP 3: CREATE CAMPAIGN
  const handleCreateCampaign = async (values) => {
    setLoading(true);
    try {
      const finalData = {
        ...values,
        contactListId: contactListId,
        scheduledAt: moment(values.scheduledAt).format("YYYY-MM-DD HH:mm:ss"),
        status: "PROGRAMADA",
      };
      await api.post("/campaigns", finalData);
      toast.success("Campanha criada e agendada com sucesso!");
      history.push("/campaigns");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Formik
            initialValues={{ name: contactListName }}
            validationSchema={ListSchema}
            onSubmit={handleCreateList}
          >
            {({ errors, touched }) => (
              <Form style={{ width: "100%", maxWidth: "400px" }}>
                <Typography variant="h5" align="center" gutterBottom style={{ fontWeight: 600, color: theme.palette.text.primary }}>

                  Nome da sua Lista de Contatos
                </Typography>
                <Typography variant="body2" align="center" color="textSecondary" style={{ marginBottom: "30px" }}>
                  Dê um nome intuitivo para identificar para quem você está enviando.
                </Typography>
                <div className={classes.formField}>
                  <Field
                    as={TextField}
                    name="name"
                    label="Ex: Clientes VIP - Março"
                    variant="outlined"
                    fullWidth
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PeopleIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className={classes.btnPrimary}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Confirmar e Ir para Importação"}
                </Button>
              </Form>
            )}
          </Formik>
        );

      case 1:
        return (
          <div style={{ width: "100%", textAlign: "center" }}>
            <Typography variant="h5" gutterBottom style={{ fontWeight: 600, color: theme.palette.text.primary }}>

              Importar Base de Dados
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "30px" }}>
              Arraste sua planilha Excel ou CSV para cá para carregar os contatos.
            </Typography>
            
            <Box display="flex" justifyContent="center">
              <div {...getRootProps()} className={classes.dropzone}>
                <input {...getInputProps()} />
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="h6" color="textPrimary">
                  {file ? file.name : (isDragActive ? "Solte o arquivo aqui..." : "Clique ou arraste o arquivo aqui")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Formatos suportados: .xlsx, .csv
                </Typography>
              </div>
            </Box>

            <a href={planilhaExemplo} download="modelo_contatos.xlsx" className={classes.templateLink}>
              <DescriptionIcon fontSize="small" /> Baixar Planilha Exemplo
            </a>

            <div style={{ marginTop: "40px" }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                className={classes.btnSecondary}
                style={{ marginRight: "10px" }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={handleUploadContacts}
                className={classes.btnPrimary}
                disabled={loading || !file}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Processar e Continuar"}
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <Formik
            initialValues={campaignData}
            validationSchema={CampaignSchema}
            onSubmit={handleCreateCampaign}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form style={{ width: "100%", maxWidth: "700px" }}>
                <Typography variant="h5" align="center" gutterBottom style={{ fontWeight: 600, color: theme.palette.text.primary }}>

                  Configurações do Envio
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <div className={classes.formField}>
                      <Field
                        as={TextField}
                        name="name"
                        label="Nome da Campanha"
                        variant="outlined"
                        fullWidth
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" fullWidth className={classes.formField}>
                      <InputLabel>Conexão (WhatsApp)</InputLabel>
                      <Field
                        as={Select}
                        name="whatsappId"
                        label="Conexão (WhatsApp)"
                        error={touched.whatsappId && Boolean(errors.whatsappId)}
                      >
                        {whatsapps.map((w) => (
                          <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <div className={classes.formField}>
                      <ModernDateTimePicker
                        label="Data/Hora do Disparo"
                        value={values.scheduledAt ? moment(values.scheduledAt).toDate() : null}
                        onChange={(newValue) => setFieldValue("scheduledAt", moment(newValue).format("YYYY-MM-DDTHH:mm"))}
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <div className={classes.formField}>
                      <Field
                        as={TextField}
                        name="message1"
                        label="Mensagem Principal"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={6}
                        error={touched.message1 && Boolean(errors.message1)}
                        helperText={touched.message1 && errors.message1}
                        placeholder="Dica: Use {nome} para personalizar a mensagem."
                      />
                    </div>
                  </Grid>
                </Grid>

                <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    className={classes.btnSecondary}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    className={classes.btnPrimary}
                    disabled={loading}
                    startIcon={<SendIcon />}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Finalizar e Agendar"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        );

      default:
        return null;
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Envio Rápido de Campanhas</Title>
      </MainHeader>
      
      <Box className={classes.root}>
        <Card className={classes.wizardCard}>
          <div className={classes.header}>
            <Title style={{ color: "white", margin: 0 }}>Assistente de Disparo Automático</Title>
            <Typography style={{ opacity: 0.9 }}>
              Transforme suas listas em resultados em apenas 3 passos simples.
            </Typography>
          </div>

          <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    style: { fontSize: "2rem" }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper elevation={0} className={classes.stepContent}>
            {renderStepContent(activeStep)}
          </Paper>
        </Card>
      </Box>

      <Dialog open={showWarning} onClose={() => setShowWarning(false)} maxWidth="sm">
        <Box className={classes.warningContainer} style={{ borderBottom: "none" }}>
          <Typography className={classes.warningTitle}>
            ⚠️ Atenção ao uso de números não oficiais para disparos
          </Typography>
          <Typography className={classes.warningText}>
            O uso de números não oficiais para envio de mensagens em massa envolve riscos significativos, incluindo bloqueios temporários ou permanentes do número, além da perda da conta.
          </Typography>
          <Typography className={classes.warningText}>
            Para reduzir esses riscos, é altamente recomendado que o número esteja “aquecido”, ou seja, sendo utilizado de forma ativa no atendimento receptivo por pelo menos 2 meses, com conversas reais e engajamento natural.
          </Typography>
          <Typography className={classes.warningTitle} style={{ fontSize: "1rem" }}>
            Caso vá iniciar disparos, siga boas práticas:
          </Typography>
          <ul className={classes.warningList}>
            <li>Comece com até 50 mensagens por dia</li>
            <li>Aumente o volume gradualmente ao longo dos dias/semanas</li>
            <li>Evite picos bruscos de envio</li>
            <li>Priorize contatos que já tiveram algum tipo de interação com o número</li>
          </ul>
          <Typography className={classes.warningText} style={{ fontStyle: "italic", fontSize: "0.85rem", opacity: 0.8 }}>
            Essas medidas ajudam a diminuir as chances de bloqueio e aumentam a vida útil do número.
          </Typography>
          
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setShowWarning(false)} className={classes.btnSecondary}>
              Cancelar
            </Button>
            <Button onClick={confirmCreateList} variant="contained" className={classes.btnPrimary}>
              Ignorar e Continuar
            </Button>
          </Box>
        </Box>
      </Dialog>
    </MainContainer>
  );
};

export default CampaignWizard;
